<template>
  <article v-if="machine" class="job-dialog-machine-item">
    <button type="button" class="machine-summary" @click="emitOpen">
      <div class="machine-image">
        <MachineModelImage :size="52" :relativePath="machineImage" />
      </div>

      <div class="machine-main">
        <div class="machine-title-row">
          <h3>{{ machineTitle }}</h3>
          <span v-if="machine.IsActive === false" class="state-chip">
            <v-icon size="14">mdi-pause-circle</v-icon>
            {{ t("text.inactive") }}
          </span>
        </div>

        <p>{{ machineSubtitle }}</p>

        <div v-if="machineFacts.length" class="machine-fact-row">
          <span v-for="fact in machineFacts" :key="fact.label" class="machine-fact">
            <v-icon size="15">{{ fact.icon }}</v-icon>
            <span>{{ fact.value }}</span>
          </span>
        </div>
      </div>

      <span class="open-button" :title="openLabel" :aria-label="openLabel">
        <v-icon size="20">mdi-chevron-right</v-icon>
      </span>
    </button>

    <div v-if="commentSections.length || addressLines.length" class="machine-details">
      <div v-if="addressLines.length" class="machine-address">
        <v-icon size="16">mdi-map-marker-outline</v-icon>
        <span>{{ addressLines.join(', ') }}</span>
      </div>

      <div v-if="commentSections.length" class="machine-comments">
        <section v-for="comment in commentSections" :key="comment.label" class="machine-comment">
          <div class="comment-label">{{ comment.label }}</div>
          <div class="comment-box">{{ comment.value }}</div>
        </section>
      </div>
    </div>
  </article>
</template>

<script setup>
import { computed, getCurrentInstance } from "vue";
import MachineModelImage from "@/components/Cards/MachineModelImage.vue";

defineOptions({ name: "JobDialogMachineItem" });

const props = defineProps({
  machineJob: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(["open"]);
const instance = getCurrentInstance();

const machine = computed(() => props.machineJob?.Machine || null);
const machineImage = computed(() => machine.value?.ModelImage || machine.value?.MachineModel?.ModelImage || undefined);

const machineTitle = computed(() => {
  const item = machine.value;
  if (!item) return "";

  return item.DisplayName
    || [item.MachineModelName, item.Name].filter(Boolean).join(" ")
    || t("text.equipment");
});

const machineSubtitle = computed(() => {
  const item = machine.value;
  if (!item) return "";

  const values = [
    item.SupplierName,
    nameFromValue(item.MachineType),
    item.MachineModelName || item.MachineModel?.Name,
  ]
    .filter(Boolean)
    .filter((value, index, array) => array.indexOf(value) === index);

  return values.length ? values.join(" / ") : "-";
});

const machineFacts = computed(() => {
  const item = machine.value;
  if (!item) return [];

  return [
    { label: "serial", icon: "mdi-barcode", value: item.SerialNumber },
    { label: "qr", icon: "mdi-qrcode", value: item.Qrcode },
  ].filter((fact) => Boolean(fact.value));
});

const addressLines = computed(() => getAddressLines(props.machineJob));

const commentSections = computed(() => {
  const item = props.machineJob || {};
  return [
    { label: t("text.error_description"), value: item.CustomerComment },
    { label: t("text.invocie_comment"), value: item.InvoiceComment },
  ].filter((comment) => Boolean(comment.value));
});

const openLabel = computed(() => [t("text.open"), machineTitle.value].filter(Boolean).join(" "));

function t(key) {
  return instance?.proxy?.$t ? instance.proxy.$t(key) : key;
}

function nameFromValue(value) {
  if (!value) return "";
  if (typeof value === "string") return value;
  return value.Name || value.DisplayName || "";
}

function getAddressLines(source) {
  if (!source) return [];
  return [
    source.CustomerDeliveryAdressName,
    source.CustomerDeliveryAdressStreet,
    [source.CustomerDeliveryAdressZip, source.CustomerDeliveryAdressCity].filter(Boolean).join(" "),
    source.CustomerDeliveryAdressCountry,
  ].filter(Boolean);
}

function emitOpen() {
  if (machine.value) {
    emit("open", machine.value);
  }
}
</script>

<style lang="scss" scoped>
.job-dialog-machine-item {
  background: #fff;
  border: 1px solid rgba(24, 39, 75, 0.1);
  border-radius: 8px;
  overflow: hidden;
  transition: border-color 0.16s ease, box-shadow 0.16s ease, transform 0.16s ease;
}

.job-dialog-machine-item:hover,
.job-dialog-machine-item:focus-within {
  border-color: rgba(30, 116, 92, 0.3);
  box-shadow: 0 10px 24px rgba(24, 39, 75, 0.08);
  transform: translateY(-1px);
}

.machine-summary {
  align-items: center;
  background: #fff;
  border: 0;
  color: inherit;
  cursor: pointer;
  display: grid;
  font: inherit;
  gap: 12px;
  grid-template-columns: auto minmax(0, 1fr) auto;
  min-width: 0;
  padding: 12px;
  text-align: left;
  width: 100%;
}

.machine-summary:focus-visible {
  outline: 2px solid rgba(30, 116, 92, 0.28);
  outline-offset: -2px;
}

.machine-image {
  align-items: center;
  background: #f6f8fa;
  border: 1px solid rgba(24, 39, 75, 0.08);
  border-radius: 8px;
  display: flex;
  height: 62px;
  justify-content: center;
  width: 62px;
}

.machine-main {
  min-width: 0;
}

.machine-title-row {
  align-items: flex-start;
  display: flex;
  gap: 8px;
  justify-content: space-between;
  min-width: 0;
}

.machine-title-row h3 {
  color: rgba(25, 34, 46, 0.92);
  display: -webkit-box;
  font-size: 15px;
  font-weight: 750;
  line-clamp: 2;
  line-height: 1.28;
  margin: 0;
  overflow: hidden;
  overflow-wrap: anywhere;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}

.machine-main p {
  color: rgba(25, 34, 46, 0.58);
  font-size: 12px;
  line-height: 1.35;
  margin: 4px 0 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.state-chip,
.machine-fact {
  align-items: center;
  border-radius: 6px;
  display: inline-flex;
  gap: 5px;
  min-width: 0;
}

.state-chip {
  background: #f2f4f7;
  color: rgba(25, 34, 46, 0.62);
  flex: 0 0 auto;
  font-size: 11px;
  font-weight: 700;
  padding: 4px 7px;
}

.machine-fact-row {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 8px;
}

.machine-fact {
  background: #f8fafc;
  border: 1px solid rgba(24, 39, 75, 0.08);
  color: rgba(25, 34, 46, 0.64);
  font-size: 12px;
  max-width: 210px;
  padding: 4px 7px;
}

.machine-fact span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.open-button {
  align-items: center;
  background: #f4f7f9;
  border-radius: 50%;
  color: rgba(30, 116, 92, 0.9);
  display: inline-flex;
  height: 34px;
  justify-content: center;
  width: 34px;
}

.machine-details {
  border-top: 1px solid rgba(24, 39, 75, 0.08);
  display: grid;
  gap: 10px;
  padding: 10px 12px 12px;
}

.machine-address {
  align-items: flex-start;
  color: rgba(25, 34, 46, 0.62);
  display: flex;
  font-size: 12px;
  gap: 6px;
  min-width: 0;
}

.machine-address span {
  min-width: 0;
  overflow-wrap: anywhere;
}

.machine-comments {
  display: grid;
  gap: 8px;
}

.comment-label {
  color: rgba(25, 34, 46, 0.58);
  font-size: 11px;
  font-weight: 750;
  margin-bottom: 4px;
  text-transform: uppercase;
}

.comment-box {
  background: #f8fafc;
  border: 1px solid rgba(24, 39, 75, 0.08);
  border-radius: 7px;
  color: rgba(25, 34, 46, 0.78);
  font-size: 13px;
  line-height: 1.45;
  max-height: 120px;
  overflow-y: auto;
  padding: 9px 10px;
  white-space: pre-wrap;
}

@media (max-width: 600px) {
  .machine-summary {
    align-items: flex-start;
    gap: 10px;
    grid-template-columns: auto minmax(0, 1fr);
    padding: 10px;
  }

  .machine-image {
    height: 54px;
    width: 54px;
  }

  .open-button {
    display: none;
  }

  .machine-fact {
    max-width: 100%;
  }
}
</style>
